import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import getPreventFromProceedFlag from 'store/product/selector/getPreventFromProceedFlag';
import { useTranslation } from 'react-i18next';
import addToBasketPredesignedProductOperation from 'store/product/operation/addToBasketPredesignedProductOperation';
import getAddingToCartFlow from 'store/product/selector/getAddingToCartFlow';
import { AddToCartFlow } from 'store/product/types';
import Spinner from 'components/Common/Spinner/Spinner';
import { BsCheck, BsExclamationCircle } from 'react-icons/bs';
import showPopupOperation from 'store/app/operation/showPopupOperation';
import { AvailablePopups } from 'store/app/types';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import gtmSendEvent from 'utils/gtm/gtmSendEvent';
import gtmPpCtaClickEvent from 'utils/gtm/gtmPpCtaClickEvent';
import getCurrentCategory from 'store/category/selector/getCurrentCategory';
import styles from './AddToBasketButtonPremium.module.scss';

const SUCCESS_ADD_TO_CART_FLOW_STATES = new Set([AddToCartFlow.addedToCart, AddToCartFlow.bundlingCompleted]);
const FAILED_ADD_TO_CART_FLOW_STATES = new Set([AddToCartFlow.failedToAddToCart, AddToCartFlow.bundlingFailed]);
const IN_PROGRESS_ADD_TO_CART_FLOW_STATES = new Set([AddToCartFlow.addingToCart, AddToCartFlow.bundlingProduct]);

interface Props {
  buttonTitle?: string;
  primary?: boolean;
  secondary?: boolean;
  openMobilePopupCheck?: () => boolean;
}

const AddToBasketButtonPremium = ({ buttonTitle, primary, secondary, openMobilePopupCheck }: Props) => {
  const dispatch = useDispatch();
  const disabled = useSelector(getPreventFromProceedFlag);
  const currentAddToCartFlow = useSelector(getAddingToCartFlow);
  const [showSuccessIcon, setShowSuccessIcon] = useState(false);
  const [redirectInProgress, setRedirectInProgress] = useState(false);
  const categoryName = (useSelector(getCurrentCategory) as any)?.title;
  const isRunning = IN_PROGRESS_ADD_TO_CART_FLOW_STATES.has(currentAddToCartFlow);
  const isFailed = FAILED_ADD_TO_CART_FLOW_STATES.has(currentAddToCartFlow);
  const isCompleted = SUCCESS_ADD_TO_CART_FLOW_STATES.has(currentAddToCartFlow);

  useEffect(() => {
    if (SUCCESS_ADD_TO_CART_FLOW_STATES.has(currentAddToCartFlow)) {
      setShowSuccessIcon(true);
      setRedirectInProgress(true);
    }
  }, [currentAddToCartFlow]);

  const { t } = useTranslation();

  const addToBasket = () => {
    gtmSendEvent(gtmPpCtaClickEvent('Add To Cart', categoryName));
    dispatch(
      openMobilePopupCheck && openMobilePopupCheck()
        ? showPopupOperation(AvailablePopups.attributeSelectionMobilePopup)
        : addToBasketPredesignedProductOperation(),
    );
  };

  return (
    <button
      id="add-to-basket-button"
      className={cn(styles.addToBasket, {
        [styles.disabled]: disabled,
        [styles.completed]: isCompleted && showSuccessIcon,
        [styles.failed]: isFailed,
        [styles.running]: isRunning || redirectInProgress,
        [styles.primary]: primary,
        [styles.secondary]: secondary,
      })}
      onClick={addToBasket}
    >
      {isRunning && <Spinner className={styles.spinner} />}
      {showSuccessIcon && <BsCheck />}
      {isFailed && <BsExclamationCircle />}
      <OpText variant="body-large-medium">{buttonTitle || t('txt_add_to_basket')}</OpText>
    </button>
  );
};

export default AddToBasketButtonPremium;
